import { EditOutlined, LockOutlined, UnlockOutlined } from "@ant-design/icons";
import React from "react";
import { AnimatedNumber, Button, Select, Tooltip } from "../../../../../../../shared/frontend/ui";
import type { StudioWithAccess } from "../../../app/api/types";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import { formatDateOnly, formatDateTimeFlexible, formatDayMonth, formatMoney } from "../../../app/units/format";
import type { UnitsSettings } from "../../../app/units/units";
import type { PersonOverviewDto } from "./usePersonOverview";
import { OverviewKpi, type OverviewKpiTone } from "./overview/OverviewKpi";
import "./personOverview.css";

type Props = {
  overview: PersonOverviewDto | undefined;
  refreshing?: boolean;
  homeStudioId?: string | null;
  canChangeHomeStudio: boolean;
  allowedStudios: StudioWithAccess[];
  saving: boolean;
  text: I18nContextValue["text"];
  units: UnitsSettings;
  /** Bestehender Pfad mit Rückfrage (`changeHomeStudio` der Akte). */
  onChangeHomeStudio: (studioId: string) => Promise<void>;
};

/** Zutrittszustand als ein Wort; der Grund steht getrennt darunter. */
function accessStatusWord(access: NonNullable<PersonOverviewDto["access"]>, text: I18nContextValue["text"]) {
  return access.status === "blocked" ? text("Person overview access blocked")
    : access.status === "not_entitled" ? text("Person overview access not entitled")
      : access.status === "limited" ? text("Person overview access limited")
        : text("Person overview access entitled");
}

function accessReasonLabel(access: NonNullable<PersonOverviewDto["access"]>, text: I18nContextValue["text"]) {
  return access.reason === "manual" ? text("Person overview access reason manual")
    : access.reason === "reminder_level" ? access.blockingReminderLevelName || null
      : access.reason === "rest_period" ? text("Person overview access reason rest period")
        : access.reason === "studio_limited" ? text("Person overview access reason studio limited")
          : access.reason === "no_basis" ? text("Person overview access reason no basis")
            : null;
}

const ACCESS_TONE: Record<NonNullable<PersonOverviewDto["access"]>["status"], OverviewKpiTone> = {
  entitled: "ok",
  limited: "warn",
  not_entitled: "warn",
  blocked: "error"
};

/**
 * Inhalt des Blocks „Lagebild“ der Personenübersicht: Kennzahlen statt Sätze — Zutritt als Ring, offener Saldo,
 * nächster Beitrag, letzter Besuch und Stammstudio. Der Grund einer Zutrittsabweichung steht als Zusatzzeile;
 * vor dem Eintreffen steht ein Strich statt einer falschen „0“.
 */
export function PersonOverviewSituation({
  overview,
  refreshing,
  homeStudioId,
  canChangeHomeStudio,
  allowedStudios,
  saving,
  text,
  units,
  onChangeHomeStudio
}: Props) {
  const [editingStudio, setEditingStudio] = React.useState(false);
  const access = overview?.access;
  const studioName = overview?.homeStudio.name
    ?? allowedStudios.find((s) => String(s.id) === String(homeStudioId ?? ""))?.name
    ?? null;
  const accessTone = access ? ACCESS_TONE[access.status] : "neutral";
  const accessReason = access ? accessReasonLabel(access, text) : null;
  const openItems = overview?.openItems;
  const finance = overview?.finance;
  const money = (value: number) => formatMoney(value, units);

  return (
    <div className="person-overview-kpis">
      {access ? (
        <div className="person-overview-kpi person-overview-kpi--access" data-tone={accessTone}>
          {/* Ring und Schloss in einem Element, damit das Symbol exakt mittig sitzt; Farbe aus dem Zustand. */}
          <span className="person-overview-access-ring" aria-hidden="true">
            {access.status === "entitled" ? <UnlockOutlined /> : <LockOutlined />}
          </span>
          <span className="person-overview-kpi-stack">
            <span className="person-overview-kpi-label">{text("Access")}</span>
            <span className="person-overview-kpi-value person-overview-kpi-value--word">{accessStatusWord(access, text)}</span>
            {accessReason ? <span className="person-overview-kpi-sub">{accessReason}</span> : null}
          </span>
        </div>
      ) : null}
      {openItems ? (
        <OverviewKpi
          label={text("Person overview open balance")}
          refreshing={refreshing}
          tone={openItems.openAmount > 0 ? (openItems.overdueCount > 0 ? "error" : "warn") : "neutral"}
        >
          <AnimatedNumber value={openItems.openAmount} format={money} />
        </OverviewKpi>
      ) : null}
      {finance ? (
        <OverviewKpi
          label={text("Person overview next contribution")}
          refreshing={refreshing}
          sub={finance.nextContributionDate ? formatDateOnly(finance.nextContributionDate, units) : undefined}
        >
          {finance.nextContributionDate ? <AnimatedNumber value={finance.nextContributionAmount ?? 0} format={money} /> : "—"}
        </OverviewKpi>
      ) : null}
      {access ? (
        <OverviewKpi label={text("Last check-in")}>
          {access.lastCheckInAt
            ? <Tooltip title={formatDateTimeFlexible(access.lastCheckInAt, units)}><span>{formatDayMonth(access.lastCheckInAt, units)}</span></Tooltip>
            : <span className="person-overview-kpi-value--word">{text("Person overview never")}</span>}
        </OverviewKpi>
      ) : null}
      <OverviewKpi label={text("Home studio")}>
        {editingStudio ? (
          <Select
            showSearch
            autoFocus
            defaultOpen
            optionFilterProp="label"
            options={allowedStudios.map((s) => ({ value: s.id, label: s.name }))}
            placeholder={text("Select studio")}
            value={homeStudioId ?? undefined}
            loading={saving}
            className="person-overview-studio-select"
            onBlur={() => setEditingStudio(false)}
            onChange={async (newId) => {
              setEditingStudio(false);
              if (!newId || String(homeStudioId ?? "") === String(newId)) return;
              await onChangeHomeStudio(String(newId));
            }}
          />
        ) : (
          <span className="person-overview-inline-value person-overview-kpi-value--word">
            <span className="person-overview-kpi-ellipsis">{studioName ?? "—"}</span>
            {canChangeHomeStudio ? (
              <Tooltip title={text("Person overview change home studio")}>
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  aria-label={text("Person overview change home studio")}
                  onClick={() => setEditingStudio(true)}
                />
              </Tooltip>
            ) : null}
          </span>
        )}
      </OverviewKpi>
    </div>
  );
}
